import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Play, Pause, Copy, Trash2, History, Zap, ArrowRight, AlertTriangle } from 'lucide-react';
import { mockAutomations, mockApps, mockTemplates } from '../mocks/automationData';
import { StatusPill } from '../components/StatusPill';
import { AppLogo } from '../components/AppLogo';
import { ConnectedAppsPanel } from '../components/ConnectedAppsPanel';
const styles = {
  btnPrimary: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]',
  btnGhost: 'text-sm text-[var(--colors-text-secondary)] bg-[var(--colors-background-card)] border border-[var(--colors-border-default)] rounded-md hover:bg-[var(--colors-neutral-50)] transition-colors',
  card: 'bg-[var(--colors-background-card)] border border-[var(--colors-border-light)] rounded-lg shadow-[var(--shadows-sm)]',
  heading: 'text-[var(--colors-text-primary)] font-semibold',
  subtext: 'text-sm text-[var(--colors-text-tertiary)]',
  input: 'w-full border border-[var(--colors-border-default)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--colors-border-focus)] bg-[var(--colors-background-card)]',
  th: 'px-6 py-3 font-medium text-[var(--colors-text-secondary)]'
};
type LoadState = 'loading' | 'error' | 'ready';
export function AutomationsList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'rules' | 'apps'>('rules');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [appFilter, setAppFilter] = useState('All');
  const [loadState, setLoadState] = useState<LoadState>('loading');
  useEffect(() => {
    const t = setTimeout(() => setLoadState('ready'), 600);
    return () => clearTimeout(t);
  }, []);
  const filtered = mockAutomations.filter(a => {
    if (statusFilter !== 'All' && a.status !== statusFilter) return false;
    if (appFilter !== 'All' && a.triggerApp !== appFilter && a.actionApp !== appFilter) return false;
    if (searchQuery && !a.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  return <div className="max-w-7xl mx-auto p-6 space-y-6" data-id="element-3007">
      <div className="flex justify-between items-center flex-wrap gap-4" data-id="element-3008">
        <div data-id="element-3009">
          <h1 className={`text-2xl ${styles.heading}`} data-id="element-3010">Automations</h1>
          <p className={`${styles.subtext} mt-1`} data-id="element-3011">
            Connect apps and automate your payment and accounting workflows.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3012">
          <button onClick={() => navigate('/automations/templates')} className={`${styles.btnGhost} px-4 py-2`} data-id="element-3013">
            Templates
          </button>
          <button onClick={() => navigate('/automations/new')} className={`${styles.btnPrimary} px-4 py-2 flex items-center gap-2`} data-id="element-3014">
            <Plus className="w-4 h-4" data-id="element-3015" /> Create Automation
          </button>
        </div>
      </div>

      <div className="flex border-b border-[var(--colors-border-light)]" data-id="element-3016">
        {(['rules', 'apps'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-transparent text-[var(--colors-text-secondary)] hover:text-[var(--colors-text-primary)]'}`} data-id="element-3017">
            {tab === 'rules' ? 'Rules' : 'Connected Apps'}
          </button>)}
      </div>

      {activeTab === 'apps' ? <ConnectedAppsPanel data-id="element-3018" /> : <div className={styles.card} data-id="element-3019">
          {/* Sticky toolbar */}
          <div className="p-4 border-b border-[var(--colors-border-light)] flex flex-wrap gap-4 items-center justify-between bg-[var(--colors-background-card)] rounded-t-lg sticky top-0 z-10" data-id="element-3020">
            <div className="flex items-center gap-4 flex-1 flex-wrap" data-id="element-3021">
              <div className="relative w-64" data-id="element-3022">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--colors-text-tertiary)]" data-id="element-3023" />
                <input type="text" placeholder="Search automations..." className={`${styles.input} pl-9`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3024" />
              </div>
              <div className="flex items-center gap-2" data-id="element-3025">
                <Filter className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-3026" />
                <select className="border border-[var(--colors-border-default)] rounded-md text-sm py-1.5 pl-3 pr-8 bg-[var(--colors-background-card)] text-[var(--colors-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3027">
                  <option value="All" data-id="element-3028">All statuses</option>
                  <option value="Active" data-id="element-3029">Active</option>
                  <option value="Paused" data-id="element-3030">Paused</option>
                  <option value="Error" data-id="element-3031">Error</option>
                  <option value="Draft" data-id="element-3032">Draft</option>
                </select>
                <select className="border border-[var(--colors-border-default)] rounded-md text-sm py-1.5 pl-3 pr-8 bg-[var(--colors-background-card)] text-[var(--colors-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]" value={appFilter} onChange={e => setAppFilter(e.target.value)} data-id="element-3033">
                  <option value="All" data-id="element-3034">All apps</option>
                  {mockApps.map(a => <option key={a.id} value={a.id} data-id="element-3035">
                      {a.name}
                    </option>)}
                </select>
              </div>
            </div>
            <button onClick={() => navigate('/automations/runs')} className="text-sm text-[var(--colors-text-secondary)] hover:text-[var(--brand-primary)] flex items-center gap-1.5 transition-colors" data-id="element-3036">
              <History className="w-4 h-4" data-id="element-3037" /> Run history
            </button>
          </div>

          {/* Loading skeleton */}
          {loadState === 'loading' && <div className="p-6 space-y-3" data-id="element-3038">
              {[0, 1, 2, 3].map(i => <div key={i} className="h-14 rounded-md bg-[var(--colors-neutral-100)] animate-pulse" data-id="element-3039" />)}
            </div>}

          {/* Error state */}
          {loadState === 'error' && <div className="px-6 py-24 text-center" data-id="element-3040">
              <div className="w-12 h-12 bg-[var(--colors-error-light)] rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-3041">
                <AlertTriangle className="w-6 h-6 text-[var(--colors-error-dark)]" data-id="element-3042" />
              </div>
              <h3 className={`text-base font-medium ${styles.heading} mb-1`} data-id="element-3043">
                Couldn’t load automations
              </h3>
              <p className={styles.subtext} data-id="element-3044">
                Something went wrong. Please try again.
              </p>
              <button onClick={() => setLoadState('ready')} className={`${styles.btnGhost} px-4 py-2 mt-6`} data-id="element-3045">
                Retry
              </button>
            </div>}

          {/* Ready */}
          {loadState === 'ready' && <div className="overflow-x-auto" data-id="element-3046">
              <table className="w-full text-left text-sm text-[var(--colors-text-secondary)]" data-id="element-3047">
                <thead className="border-b border-[var(--colors-border-light)] bg-[var(--colors-neutral-50)]" data-id="element-3048">
                  <tr data-id="element-3049">
                    <th className={styles.th} data-id="element-3050">Name & flow</th>
                    <th className={styles.th} data-id="element-3051">Status</th>
                    <th className={styles.th} data-id="element-3052">Runs (30d)</th>
                    <th className={styles.th} data-id="element-3053">Last run</th>
                    <th className="px-6 py-3 font-medium text-[var(--colors-text-secondary)] text-right" data-id="element-3054">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-3055">
                  {filtered.length === 0 ? <tr data-id="element-3056">
                      <td colSpan={5} className="px-6 py-24 text-center" data-id="element-3057">
                        <div className="flex flex-col items-center justify-center" data-id="element-3058">
                          <div className="w-12 h-12 bg-[var(--colors-neutral-50)] rounded-full flex items-center justify-center mb-4" data-id="element-3059">
                            <Zap className="w-6 h-6 text-[var(--colors-text-tertiary)]" data-id="element-3060" />
                          </div>
                          <h3 className={`text-base font-medium ${styles.heading} mb-1`} data-id="element-3061">
                            Create your first automation
                          </h3>
                          <p className={styles.subtext} data-id="element-3062">
                            Wire payment events to your accounting apps in
                            minutes.
                          </p>
                          <button onClick={() => navigate('/automations/new')} className={`${styles.btnPrimary} px-4 py-2 mt-6 flex items-center gap-2`} data-id="element-3063">
                            <Plus className="w-4 h-4" data-id="element-3064" /> Create automation
                          </button>
                          <div className="mt-6 flex flex-wrap justify-center gap-2" data-id="element-3065">
                            {mockTemplates.slice(0, 4).map(t => <button key={t.id} onClick={() => navigate('/automations/new')} className="px-3 py-1.5 bg-[var(--colors-background-card)] border border-[var(--colors-border-default)] rounded-full text-xs text-[var(--colors-text-secondary)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors" data-id="element-3066">
                                {t.name}
                              </button>)}
                          </div>
                        </div>
                      </td>
                    </tr> : filtered.map(auto => <tr key={auto.id} onClick={() => navigate(`/automations/${auto.id}`)} className="border-b border-[var(--colors-neutral-100)] last:border-0 hover:bg-[var(--colors-neutral-50)] cursor-pointer" data-id="element-3067">
                        <td className="px-6 py-4" data-id="element-3068">
                          <div className="font-medium text-[var(--colors-text-primary)] mb-1.5" data-id="element-3069">
                            {auto.name}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[var(--colors-text-tertiary)]" data-id="element-3070">
                            <AppLogo appId={auto.triggerApp} size="sm" data-id="element-3071" />
                            <span className="truncate max-w-[140px]" data-id="element-3072">
                              {auto.triggerEvent}
                            </span>
                            <ArrowRight className="w-3 h-3" data-id="element-3073" />
                            <AppLogo appId={auto.actionApp} size="sm" data-id="element-3074" />
                            <span className="truncate max-w-[140px]" data-id="element-3075">
                              {auto.actionEvent}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-3076">
                          <StatusPill status={auto.status} data-id="element-3077" />
                        </td>
                        <td className="px-6 py-4" data-id="element-3078">
                          <div className="font-mono" data-id="element-3079">
                            {auto.runs30d.toLocaleString()}
                          </div>
                          {auto.runs30d > 0 && <div className="text-xs mt-0.5" style={{
                  color: auto.successRate >= 95 ? 'var(--colors-success-dark)' : 'var(--colors-error-dark)'
                }} data-id="element-3080">
                              {auto.successRate}% success
                            </div>}
                        </td>
                        <td className="px-6 py-4" data-id="element-3081">
                          {auto.lastRunTime ? new Date(auto.lastRunTime).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-6 py-4" data-id="element-3082">
                          <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()} data-id="element-3083">
                            <button className="p-1.5 rounded hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] transition-colors" title={auto.status === 'Active' ? 'Pause' : 'Enable'} data-id="element-3084">
                              {auto.status === 'Active' ? <Pause className="w-4 h-4" data-id="element-3085" /> : <Play className="w-4 h-4" data-id="element-3086" />}
                            </button>
                            <button onClick={() => navigate(`/automations/${auto.id}`)} className="p-1.5 rounded hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] transition-colors" title="Duplicate" data-id="element-3087">
                              <Copy className="w-4 h-4" data-id="element-3088" />
                            </button>
                            <button onClick={() => navigate('/automations/runs')} className="p-1.5 rounded hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] transition-colors" title="Run history" data-id="element-3089">
                              <History className="w-4 h-4" data-id="element-3090" />
                            </button>
                            <button className="p-1.5 rounded hover:bg-[var(--colors-error-light)] text-[var(--colors-error-default)] hover:text-[var(--colors-error-dark)] transition-colors" title="Delete" data-id="element-3091">
                              <Trash2 className="w-4 h-4" data-id="element-3092" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
    </div>;
}